/**
 * Label and CSV helpers shared by the people search surfaces (tio-admin
 * `/people`, tio-employee `/admin/employees`). Facet selections travel as
 * comma-separated query params, and every facet label carries its bucket count.
 */

/**
 * Append a bucket count to a facet label. A missing count renders the bare
 * label — an aggregation that returned nothing at all is different from one
 * that returned zero, and only the latter should show "(0)".
 */
export function withCount(label: string, count: number | undefined): string {
  return count === undefined ? label : `${label} (${count})`;
}

/** Split a facet query param into its selected keys, dropping empties. */
export function csvToKeys(csv: string | undefined): string[] {
  return csv?.split(',').filter(Boolean) ?? [];
}

/** The CSV with one value removed — how a filter chip's X button unsets itself. */
export function csvWithout(csv: string | undefined, value: string): string {
  return csvToKeys(csv)
    .filter((existing) => existing !== value)
    .join(',');
}

/**
 * Translate a facet value, falling back to `fallback` when the key is absent.
 * Facet values come from the search index, so a newly-indexed enum member
 * reaches the UI before anyone adds its label — showing the raw value beats
 * showing `Missing translation "people.jurisdictions.NEW_ROLE"`.
 *
 * Presence is tested with `exists`, not by comparing `t`'s output to the key:
 * ember-intl returns a "Missing translation …" sentence for an unknown key, so
 * the comparison never matches and the fallback never fires.
 */
export function facetLabel(
  intl: { exists: (key: string) => boolean; t: (key: string) => string },
  key: string,
  fallback: string
): string {
  return intl.exists(key) ? intl.t(key) : fallback;
}
